import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { IConfig } from '../../Interfaces/IConfig';
import { customFetcher } from '../../hooks/useFetch';
import { PublicLayout } from './PublicLayout';

interface IProps {
  configObject: IConfig;
}

type State = 'loading' | 'success' | 'error';

/**
 * Bestätigungsseite für das Zurücksetzen des Passworts. Der Mandant gelangt
 * über den Link aus der Bestätigungs-E-Mail (SystemMail 22) hierher. Allein
 * der Aufruf des GET-Endpunkts bestätigt die Anfrage: Das Backend prüft den
 * Token, erzeugt ein temporäres Passwort und versendet die Folge-E-Mail
 * (SystemMail 3) mit dem Auto-Login-Link ins Mandantenportal.
 */
export const ResetPasswordConfirm: React.FC<IProps> = ({ configObject }) => {
  const params = useParams<{ idContact: string; token: string }>();
  const [state, setState] = useState<State>('loading');
  const hasRequested = useRef(false);

  useEffect(() => {
    // Strict-Mode ruft Effekte doppelt auf; der Reset darf nur einmal
    // ausgelöst werden.
    if (hasRequested.current) {
      return;
    }
    hasRequested.current = true;

    if (!params.idContact || !params.token) {
      setState('error');
      return;
    }

    customFetcher({
      endpoint: `/contact/requestNewPassword/${params.idContact}/${params.token}`,
    })
      .then(() => setState('success'))
      .catch(() => setState('error'));
  }, [params.idContact, params.token]);

  return (
    <PublicLayout
      configObject={configObject}
      title='Passwort zurücksetzen'
      subtitle='Wir bestätigen Ihre Anfrage zum Zurücksetzen des Passworts.'
    >
      {state === 'loading' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography variant='body2' color='text.secondary'>
            Anfrage wird bestätigt …
          </Typography>
        </Box>
      )}

      {state === 'success' && (
        <Alert severity='success' icon={<CheckCircleOutline fontSize='small' />}>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>Anfrage bestätigt.</strong>
          </Typography>
          <Typography variant='body2'>
            Sie erhalten in Kürze eine E-Mail mit einem Link, über den Sie sich
            automatisch anmelden und ein neues Passwort vergeben können.
          </Typography>
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: 'block', mt: 1 }}
          >
            Falls keine E-Mail eintrifft, prüfen Sie bitte den Spam-Ordner oder
            wenden Sie sich an Ihr Notarbüro.
          </Typography>
        </Alert>
      )}

      {state === 'error' && (
        <Alert severity='warning'>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>Das hat leider nicht geklappt.</strong>
          </Typography>
          <Typography variant='body2'>
            Das Passwort kann nicht zurückgesetzt werden oder die Anfrage ist
            bereits abgelaufen. Bitte fordern Sie das Zurücksetzen erneut an. In
            dringenden Fällen kontaktieren Sie uns bitte.
          </Typography>
        </Alert>
      )}
    </PublicLayout>
  );
};
